import React, { useState } from "react";
import {
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  TagLabel,
  Box,
  Flex,
  Tooltip,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { useAppContext } from "../../contexts/AppContext";
import { CircleFlag } from "react-circle-flags";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";

const FundTable = ({ data }) => {
  const { formatMillions, tableSize } = useAppContext();
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: "fundName", direction: "asc" });
  const [selectedFunds, setSelectedFunds] = useState([]);

  const handleRowClick = (fund) => {
    const formattedFundName = fund.fundName.replace(/\s+/g, "-");
    navigate(`/funds/${formattedFundName}`);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCheckboxChange = (e, index) => {
    e.stopPropagation();
    setSelectedFunds((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleSelectAll = () => {
    if (selectedFunds.length === data.length) {
      setSelectedFunds([]);
    } else {
      setSelectedFunds(data.map((_, i) => i));
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aVal = sortConfig.key === "investmentStrategy" ? a.investmentStrategy?.name : a[sortConfig.key];
    const bVal = sortConfig.key === "investmentStrategy" ? b.investmentStrategy?.name : b[sortConfig.key];

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <TableContainer>
      <Table
        className="hasCheckbox"
        variant="simple"
        size={tableSize}
        sx={{
          "th > div": {
            display: "flex",
            alignItems: "center",
            gap: 1,
          },
          "th[data-is-numeric=true] > div": {
            justifyContent: "flex-end",
          },
        }}
      >
        <Thead>
          <Tr>
            <Th>
              <Checkbox
                isChecked={selectedFunds.length === data.length && data.length > 0}
                isIndeterminate={selectedFunds.length > 0 && selectedFunds.length < data.length}
                onChange={handleSelectAll}
              />
            </Th>
            {[
              { key: "fundName", label: "Subfund" },
              { key: "parentFund", label: "Fund" },
              { key: "navFrequency", label: "NAV Frequency" },
              { key: "navValue", label: "NAV", tooltip: "Net Asset Value", isNumeric: true },
              { key: "investmentStrategy", label: "Investment Strategy" },
              { key: "fundCurrency", label: "Fund Currency" },
              { key: "commitment", label: "Commitment", isNumeric: true },
              { key: "commitmentPercentage", label: "% Commitment", isNumeric: true },
              { key: "totalCommitment", label: "Total Commitment", isNumeric: true },
            ].map(({ key, label, tooltip, isNumeric }) => (
              <Th
                key={key}
                isNumeric={isNumeric}
                onClick={() => key !== "fundCurrency" && handleSort(key)}
                cursor={key !== "fundCurrency" ? "pointer" : "default"}
              >
                <Flex>
                  {tooltip ? <Tooltip label={tooltip}>{label}</Tooltip> : label}
                  {sortConfig.key === key &&
                    (sortConfig.direction === "asc" ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    ))}
                </Flex>
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {sortedData.length > 0 ? (
            sortedData.map((fund, index) => {
              const commitmentPercentage = fund.totalCommitment
                ? ((fund.commitment / fund.totalCommitment) * 100).toFixed(2)
                : "0.00";

              return (
                <Tr
                  key={index}
                  onClick={() => handleRowClick(fund)}
                  _hover={{ bg: "gray.50" }}
                  cursor="pointer"
                >
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={selectedFunds.includes(index)}
                      onChange={(e) => handleCheckboxChange(e, index)}
                    />
                  </Td>
                  <Td fontWeight="500">{fund.fundName}</Td>
                  <Td>
                    <Tag colorScheme="brand">{fund.parentFund}</Tag>
                  </Td>
                  <Td>
                    <Tag size="sm">{fund.navFrequency}</Tag>
                  </Td>
                  <Td isNumeric>{formatMillions(fund.navValue)}</Td>
                  <Td>
                    <Tag gap={1}>
                      <Box
                        as="span"
                        bg={fund.investmentStrategy.color}
                        h="6px"
                        w="6px"
                        borderRadius="full"
                      />
                      <TagLabel>{fund.investmentStrategy.name}</TagLabel>
                    </Tag>
                  </Td>
                  <Td>
                    <Tag gap={1}>
                      <CircleFlag
                        countryCode={fund.fundCurrency.country}
                        style={{
                          height: "16px",
                          width: "16px",
                          border: "1px solid #DDD",
                          borderRadius: "50%",
                        }}
                      />
                      {fund.fundCurrency.ISO}
                    </Tag>
                  </Td>
                  <Td isNumeric>{formatMillions(fund.commitment)}</Td>
                  <Td isNumeric>{commitmentPercentage} %</Td>
                  <Td isNumeric>{formatMillions(fund.totalCommitment)}</Td>
                </Tr>
              );
            })
          ) : (
            <Tr>
              <Td colSpan="10">
                <Text textAlign="center" py={10} color="gray.500">
                  No funds match your filters
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default FundTable;
